import { FunctionComponent, useMemo } from 'react';

import styled from 'styled-components';

import { MOBILE_BREAKPOINT } from 'core/hooks';

import { useDataTableActions } from '../../useDataTableActions';
import { useDataTableExtras } from '../../useDataTableExtras';
import { ActionIcon } from './ActionIcon';
import { ActionListCellProps } from './ActionListCellProps';

export const ActionListCell: FunctionComponent<ActionListCellProps> = ({ className, dataItem, dataIndex }) => {
  const actions = useDataTableActions();
  const { dataItemKey } = useDataTableExtras();

  const content = useMemo(
    () => (
      <td className={className}>
        <StyledButtonsContainer>
          {actions?.map((action) => (
            <ActionIcon
              key={action.key}
              action={action}
              dataItem={dataItem}
              dataItemIndex={dataIndex}
              dataItemKey={dataItemKey}
              testingPrefix={`${ActionListCell.displayName}_action-button_${action.key}`}
            />
          ))}
        </StyledButtonsContainer>
      </td>
    ),
    [actions, className, dataIndex, dataItem, dataItemKey],
  );

  return content;
};

ActionListCell.displayName = 'ActionListCell';

const StyledButtonsContainer = styled.div`
  display: flex;
  flex-wrap: nowrap; /* Prevent line breaking on desktop */
  justify-content: flex-start;
  align-items: center;
  gap: ${({ theme }) => theme.space.spacing10};

  /* Mobile-specific styling using consistent MOBILE_BREAKPOINT (768px) */
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-wrap: wrap; /* Allow wrapping on mobile for max 3 buttons per row */
    gap: ${({ theme }) => theme.space.spacing10}; /* Consistent spacing */
    padding: ${({ theme }) => theme.space.spacing10} 0; /* Vertical padding for easier touch */
    justify-content: flex-start; /* Keep left-aligned */

    /* Ensure buttons take appropriate width for 3 per row */
    /* With gap of spacing10 (8px), 3 buttons should fit nicely */
    max-width: 100%;

    /* Ensure proper minimum height for touch accessibility */
    min-height: 44px;

    /* Prevent buttons from being too close to cell edges on mobile */
    padding-left: ${({ theme }) => theme.space.spacing10};
    padding-right: ${({ theme }) => theme.space.spacing10};
  }
`;
